import qs from 'qs';
import { telemetry } from '../backend';

let history;
let routesConfiguration;

export const getUrlForRouteWithGivenParameters = (
  givenRouteName,
  parameters = {},
) => {
  const configuration = routesConfiguration.find(
    ([ROUTE_NAME]) => givenRouteName === ROUTE_NAME,
  );
  if (!configuration) {
    telemetry(
      telemetry.ERROR,
      `[getUrlForRouteWithGivenParameters] givenRouteName of ${givenRouteName} does not correspond to an actual route.`,
    );
  }
  const [, ROUTE_URL] = configuration;
  let url = ROUTE_URL;
  const remainingParameters = { ...parameters };
  const parameterNames = (ROUTE_URL.match(/(:\w*)/g) || []).map((parameter) =>
    parameter.replace(':', ''),
  );
  parameterNames.forEach((parameterName) => {
    if (!parameters[parameterName]) {
      telemetry(
        telemetry.ERROR,
        `[getUrlForRouteWithGivenParameters] missing parameter ${parameterName} while changing to ${givenRouteName}. Given parameters ${JSON.stringify(
          parameters,
        )}. Please check the stacktrace to track down the originator for this bad call.`,
      );
    }
    url = url.replace(`:${parameterName}`, parameters[parameterName]);
    delete remainingParameters[parameterName];
  });
  if (remainingParameters) {
    url += `?${qs.stringify(remainingParameters)}`;
  }

  let replaceHistory;
  if (configuration[5]) {
    replaceHistory = configuration[5].replaceHistory;
  }

  return [url, replaceHistory];
};

export const transitionToNextRoute = (
  givenRouteName,
  parameters,
  replaceHistory,
) => {
  telemetry(
    telemetry.DEBUG,
    `[transitionToNextRoute] request for transition to ${givenRouteName} with parameters ${JSON.stringify(
      parameters,
    )}.`,
  );
  const [url, configReplaceHistory] = getUrlForRouteWithGivenParameters(
    givenRouteName,
    parameters,
  );

  // Check to see if replaceHistory is true, or that it is undefined, but the default route configuration has it set to true.
  // This allows for calling `transitionToNextRoute()` to override the route config
  if (
    replaceHistory ||
    (replaceHistory === undefined && configReplaceHistory)
  ) {
    history.replace(url);
  } else {
    history.push(url);
  }
};

/**
 * This helper method attempts to make it more transparent in components when transitioning to routes which replace history.
 */
export const replaceHistoryToNextRoute = (givenRouteName, parameters) =>
  transitionToNextRoute(givenRouteName, parameters, true);

/**
 * Transitions to the url with no checking for validity. Use cautiously.
 *
 * @param {string} url
 */
export const transitionToRawURL = (url) => {
  telemetry(
    telemetry.DEBUG,
    `[transitionToRawURL] request for transition to ${url}`,
  );
  history.push(url);
};

/**
 * Reloads to a new URL and maintains the existing search and hash parameters.
 * This is required in order to keep the version of cordova in the url
 *
 * @param {string} url
 */
export const reloadToRawURL = (url) => {
  // Parse the url passed in so we can get search and hash params
  const parsedUrl = new URL(url, window.location.origin);

  // Merge any passed in search params with the existing search params
  const newSearchParamsArray = parsedUrl.replace('?', '').split('&');
  const existingSearchParamsArray = window.location.search
    .replace('?', '')
    .split('&');
  const combinedSearchParamsArray = [
    ...newSearchParamsArray,
    ...existingSearchParamsArray,
  ];
  const searchParams =
    combinedSearchParamsArray.length > 0
      ? `?${combinedSearchParamsArray.join('&')}`
      : '';

  telemetry(
    telemetry.DEBUG,
    `[reloadToRawURL] request reload to ${url} with search and hash params`,
  );
  window.location = `${url}${searchParams}${
    parsedUrl.hash || window.location.hash
  }`;
};

export const transitionToPreviousRoute = () => {
  history.goBack();
};

export const initialize = ({
  history: givenHistory,
  routesConfiguration: givenRoutesConfiguration,
}) => {
  history = givenHistory;
  routesConfiguration = givenRoutesConfiguration;
};
